import { useEffect, useState } from 'react'
import { MessageCircle, X } from 'lucide-react'

interface LauncherConfig {
  brandColor: string
  unreadCount: number
}

// Get initial brand color from URL params (instant, no flash)
function getInitialBrandColor(): string {
  const params = new URLSearchParams(window.location.search)
  return params.get('brandColor') || '#0066FF'
}

export default function Launcher() {
  const [config, setConfig] = useState<LauncherConfig>({
    brandColor: getInitialBrandColor(),
    unreadCount: 0,
  })
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type } = event.data

      switch (type) {
        case 'signal:launcher:init':
          setConfig(event.data.config)
          break
        case 'signal:launcher:updateBadge':
          setConfig(prev => ({ ...prev, unreadCount: event.data.count }))
          break
        case 'signal:launcher:setOpen':
          setIsOpen(event.data.isOpen)
          break
      }
    }

    window.addEventListener('message', handleMessage)

    // Request init from parent (if in iframe)
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'signal:launcher:requestInit' }, '*')
      window.parent.postMessage({ type: 'signal:launcher:ready' }, '*')
    }

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const handleClick = () => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'signal:launcher:click' }, '*')
    }
  }

  const showBadge = config.unreadCount > 0 && !isOpen

  const buttonStyle: React.CSSProperties = {
    backgroundColor: config.brandColor,
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="relative">
        <button
          onClick={handleClick}
          className="flex items-center justify-center w-[48px] h-[48px] rounded-full shadow-lg transition-transform duration-200 hover:scale-110 active:scale-95 cursor-pointer border-0"
          style={buttonStyle}
          aria-label={isOpen ? 'Fechar chat' : 'Abrir chat'}
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <MessageCircle className="w-6 h-6 text-white" />
          )}
        </button>

        {/* Unread badge with white ring for cutout effect */}
        {showBadge && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full pointer-events-none ring-2 ring-white">
            {config.unreadCount > 99 ? '99+' : config.unreadCount}
          </span>
        )}
      </div>
    </div>
  )
}
